import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, Plus, Pencil, Trash2, Store, Loader2, Phone, MapPin } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';

const EMPTY_SUPPLIER = { name: '', pic: '', phone: '', address: '', status: 'active' };

export default function SuppliersPage() {
  const { can } = useAuth();
  const canWrite = can('business.write');
  const canDelete = can('business.delete');
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/suppliers', { params: { search: search || undefined } })).data); } catch (e) { toast.error('Gagal memuat'); } finally { setLoading(false); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="animate-in-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><Store className="w-6 h-6 text-[var(--fpccs-primary)]" /> Master Supplier</h1>
          <p className="text-sm text-muted-fpccs mt-1">Kelola daftar supplier untuk mempercepat input transaksi pengeluaran.</p>
        </div>
        {canWrite && <Button data-testid="btn-add-supplier" onClick={() => { setEditing(null); setOpen(true); }} className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">
          <Plus className="w-4 h-4 mr-1.5" /> Tambah Supplier
        </Button>}
      </div>

      <div className="card-fpccs p-4 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
          <Input data-testid="supplier-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari supplier atau PIC..." className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]" />
        </div>
      </div>

      <div className="card-fpccs overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[var(--fpccs-border)] hover:bg-transparent">
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Nama Supplier</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">PIC</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Telepon</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Alamat</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Status</TableHead>
              <TableHead className="w-24 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [...Array(5)].map((_, i) => <TableRow key={i}>{[...Array(6)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>) :
             items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-fpccs py-10">Belum ada supplier.</TableCell></TableRow> :
             items.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`} className="border-b border-[var(--fpccs-border)] hover:bg-white/[0.03]">
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-xs">{s.pic || '-'}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{s.phone || '-'}</TableCell>
                <TableCell className="text-xs text-muted-fpccs max-w-[240px] truncate">{s.address || '-'}</TableCell>
                <TableCell><span className={`text-[11px] px-1.5 py-0.5 rounded border ${s.status === 'active' ? 'bg-[rgba(47,191,113,0.14)] text-[#2FBF71] border-[rgba(47,191,113,0.28)]' : 'bg-[rgba(224,90,79,0.14)] text-[#E05A4F] border-[rgba(224,90,79,0.28)]'}`}>{s.status || 'active'}</span></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canWrite && <Button size="icon" variant="ghost" data-testid={`btn-edit-supplier-${s.id}`} onClick={() => { setEditing(s); setOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>}
                    {canDelete && <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" data-testid={`btn-delete-supplier-${s.id}`} className="text-[var(--fpccs-danger)]"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Hapus supplier?</AlertDialogTitle><AlertDialogDescription>Tindakan tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={async () => { await api.delete(`/suppliers/${s.id}`); toast.success('Terhapus'); load(); }} className="bg-[var(--fpccs-danger)] text-[var(--fpccs-bg)]">Hapus</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <SupplierFormDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}

function SupplierFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY_SUPPLIER, ...initial } : EMPTY_SUPPLIER);
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nama wajib'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/suppliers/${initial.id}`, form);
      else await api.post('/suppliers', form);
      toast.success(isEdit ? 'Diperbarui' : 'Ditambahkan');
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error('Gagal'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="supplier-form-dialog" className="max-w-md bg-[var(--fpccs-surface-1)]">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Supplier' : 'Tambah Supplier'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Nama Supplier *</Label><Input data-testid="supplier-name-input" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">PIC</Label><Input data-testid="supplier-pic-input" value={form.pic} onChange={(e) => setForm(f => ({ ...f, pic: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Telepon</Label><Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Alamat</Label><Textarea rows={2} data-testid="supplier-address-input" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="supplier-status-input"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Non-aktif</SelectItem></SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-supplier" className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Simpan'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
